import type { BillingAccount, BillingEstimate } from '~/gql/billing/billing'
import { useMutation } from '@apollo/client/react'
import { BillingPaymentTerm, BillingTier } from '@zeus/index'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Label } from '~/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { GQL_CHANGE_BILLING_PLAN } from '~/gql/billing/billing'
import { client } from '~/helpers/apollo'
import { formatCents } from '~/pages/backoffice/admin/billing/money'

const TIERS = [
  BillingTier.FREE,
  BillingTier.S,
  BillingTier.M,
  BillingTier.L,
  BillingTier.XL,
  BillingTier.CUSTOM,
]
const TERMS = [BillingPaymentTerm.UPFRONT, BillingPaymentTerm.MONTHLY]

type Props = {
  account?: BillingAccount
  /**
   * Estimation du mois COURANT, jamais celle du mois observé : le plancher
   * sélectionnable dépend du compteur d'aujourd'hui, pas d'un mois qu'on feuillette.
   */
  estimate?: BillingEstimate
  onChanged: () => void
}

/**
 * Palier souscrit (le plancher) et échéancier, à la main de l'admin — titulaire
 * du contrat, sans validation plateforme. Effet immédiat, sans prorata : rien
 * n'est encaissé ici. Le palier n'est jamais un plafond : aucune session n'est
 * bloquée quel que soit le compteur (BUSINESS_MODEL.md).
 *
 * ⚠️ L'échéancier n'est PAS un choix de prix : un palier coûte la même chose des
 * deux côtés, seule l'échéance change (deux mois offerts au comptant).
 *
 * Le champ reste vide tant que le compte n'est pas chargé : afficher « Gratuit »
 * par défaut ferait lire à l'admin une formule qui n'est pas la sienne.
 */
function PlanCard({ account, estimate, onChanged }: Props) {
  const { t, i18n } = useTranslation()
  const [tier, setTier] = useState<BillingTier | ''>('')
  const [term, setTerm] = useState<BillingPaymentTerm>(BillingPaymentTerm.UPFRONT)
  const [changePlan, { loading }] = useMutation(GQL_CHANGE_BILLING_PLAN, { client })

  useEffect(() => {
    if (!account) return
    setTier(account.tier)
    setTerm(account.paymentTerm)
  }, [account])

  const paid = tier !== '' && tier !== BillingTier.FREE
  const isCustom = tier === BillingTier.CUSTOM
  const money = (cents?: number | null) => (cents == null ? '—' : formatCents(cents, i18n.language))

  /**
   * Souscrire SOUS le palier effectif n'a aucun effet : le facturé vaut
   * `max(souscrit, effectif)`. Griser ces paliers évite de laisser croire à une
   * redescente qui n'a pas lieu — c'est un choix inopérant, pas un blocage
   * commercial : les apprenants, eux, ne sont jamais empêchés de rien.
   */
  const floor = estimate?.effectiveTier ?? BillingTier.FREE
  const belowFloor = (value: BillingTier) => TIERS.indexOf(value) < TIERS.indexOf(floor)
  const flooredOut = tier !== '' && belowFloor(tier)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (tier === '' || flooredOut) return
    try {
      await changePlan({
        variables: { input: { tier, paymentTerm: paid ? term : undefined } },
      })
      onChanged()
      toast.success(t('billing.plan.saved'))
    } catch {
      toast.error(t('billing.plan.error'))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('billing.plan.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="billing-tier">{t('billing.plan.field')}</Label>
              <Select
                value={tier}
                onValueChange={value => setTier(value as BillingTier)}
                disabled={!account}
              >
                <SelectTrigger id="billing-tier">
                  <SelectValue placeholder={t('billing.plan.loading')} />
                </SelectTrigger>
                <SelectContent>
                  {TIERS.map(value => (
                    <SelectItem key={value} value={value} disabled={belowFloor(value)}>
                      {t(`billing.tier.${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {tier !== '' && (
                <p className="text-xs text-muted-foreground">{t(`billing.tier.${tier}.help`)}</p>
              )}
            </div>

            {/* Le prix est déjà fixé par le palier : il ne reste que l'échéance. */}
            {paid && !isCustom && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="billing-term">{t('billing.term.field')}</Label>
                <Select value={term} onValueChange={value => setTerm(value as BillingPaymentTerm)}>
                  <SelectTrigger id="billing-term">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TERMS.map(value => (
                      <SelectItem key={value} value={value}>
                        {t(`billing.term.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t(`billing.term.${term}.help`)}</p>
              </div>
            )}
          </div>

          {/* Prix du palier facturé (mensuel plat + année au comptant). */}
          {estimate && !estimate.isCustom && estimate.monthlyPriceCents != null && (
            <dl className="grid gap-2 rounded-md border p-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">{t('billing.tier.monthlyPrice')}</dt>
                <dd className="font-medium tabular-nums">{money(estimate.monthlyPriceCents)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t('billing.tier.upfrontYear')}</dt>
                <dd className="font-medium tabular-nums">{money(estimate.upfrontYearlyAmountCents)}</dd>
              </div>
            </dl>
          )}

          {/* Le compteur impose déjà son palier : dire lequel, et pourquoi les
              paliers en dessous sont grisés. */}
          {flooredOut && estimate && (
            <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
              {t('billing.plan.belowFloor', {
                learners: estimate.learnerCount,
                tier: t(`billing.tier.${floor}`),
              })}
            </p>
          )}

          {isCustom && (
            <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
              {t('billing.tier.CUSTOM.notice')}
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">{t('billing.plan.noPaymentYet')}</p>
            <Button type="submit" disabled={loading || !account || flooredOut}>
              {t('billing.plan.save')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default PlanCard
